import React from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { Input, Button, message } from "antd";
import { useToaster } from "@components";
import { useFormik } from "formik";
import * as Yup from "yup";

import { usersLoginPost } from "../../libs/apis";
import { useModulesBackupStore, useRouterStore } from "../../libs/stores";
import { LoadingFullPage } from "./Loading";

import CenefaLogin from "../images/logo.png";
import { AxiosError } from "axios";
import "../css/login.css";

// ─── Canvas animado ──────────────────────────────────────────────────────────
const AnimatedBackground: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    let animId: number;

    const COLORS = ["#1e1948", "#7F77DD", "#3C3489", "#1D9E75", "#5DCAA5"];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    type Dot = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      color: string;
      alpha: number;
    };
    const dots: Dot[] = Array.from({ length: 65 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 1.8 + 0.5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: Math.random() * 0.55 + 0.2,
    }));

    const drawGrid = () => {
      const sp = 48;
      ctx.save();
      ctx.strokeStyle = "rgba(127,119,221,0.06)";
      ctx.lineWidth = 0.5;
      for (let x = 0; x < canvas.width; x += sp) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += sp) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      ctx.restore();
    };

    let t = 0;
    const drawPulse = () => {
      const cx = canvas.width * 0.5,
        cy = canvas.height * 0.5;
      const maxR = Math.max(canvas.width, canvas.height) * 0.65;
      [0, 1, 2].forEach((i) => {
        const r = (((t * 0.3 + i * 100) % 300) / 300) * maxR;
        const alpha = (1 - r / maxR) * 0.055;
        ctx.save();
        ctx.strokeStyle = `rgba(83,74,183,${alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      });
    };

    const drawConnections = () => {
      const maxDist = 135;
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const a = (1 - dist / maxDist) * 0.2;
            ctx.save();
            ctx.strokeStyle = `rgba(127,119,221,${a})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
            ctx.restore();
          }
        }
      }
    };

    const animate = () => {
      t++;
      const w = canvas.width,
        h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const grad = ctx.createRadialGradient(
        w * 0.5,
        h * 0.5,
        0,
        w * 0.5,
        h * 0.5,
        w * 0.8,
      );
      grad.addColorStop(0, "#1a1640");
      grad.addColorStop(1, "#0e0c22");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      const accent = ctx.createRadialGradient(w, h, 0, w, h, w * 0.55);
      accent.addColorStop(0, "rgba(29,158,117,0.12)");
      accent.addColorStop(1, "rgba(29,158,117,0)");
      ctx.fillStyle = accent;
      ctx.fillRect(0, 0, w, h);

      drawGrid();
      drawPulse();
      drawConnections();

      dots.forEach((d) => {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0) d.x = w;
        if (d.x > w) d.x = 0;
        if (d.y < 0) d.y = h;
        if (d.y > h) d.y = 0;
        ctx.save();
        ctx.globalAlpha = d.alpha;
        ctx.fillStyle = d.color;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="login-canvas" />;
};

// ─── Login ───────────────────────────────────────────────────────────────────
export const Login: React.FC = () => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const tokens = localStorage.getItem(process.env.REACT_APP_TOKEN!);
  const setModules = useRouterStore((s) => s.setModules);
  const setModulesBackup = useModulesBackupStore((s) => s.setModulesBackup);
  const setFirstName = useRouterStore((s) => s.setFirstName);
  const setProfileId = useRouterStore((s) => s.setProfileId);
  const setClientId = useRouterStore((s) => s.setClientId);
  const setClient = useRouterStore((s) => s.setClient);
  const setTypeDashboardCode = useRouterStore((s) => s.setTypeDashboardCode);
  const setAvatar = useRouterStore((s) => s.setAvatar);
  const setSubscriptionStatus = useRouterStore((s) => s.setSubscriptionStatus);
  const setApiKeyMagnoSri = useRouterStore((s) => s.setApiKeyMagnoSri);
  const setUserMagnoSri = useRouterStore((s) => s.setUserMagnoSri);
  const setPasswordMagnoSri = useRouterStore((s) => s.setPasswordMagnoSri);
  const setClientDocNumber = useRouterStore((s) => s.setClientDocNumber);
  const setRole = useRouterStore((s) => s.setRole);

  const [isProcessing, setProcessing] = React.useState(false);
  const [continuo, setContinuo] = React.useState(false);

  React.useEffect(() => {
    if (tokens) navigate("/dashboard", { replace: true });
  }, [tokens, navigate]);

  React.useEffect(() => {
    if (continuo) {
      setContinuo(false);
      navigate("/dashboard", { replace: true });
    }
  }, [continuo, navigate]);

  const formik = useFormik({
    initialValues: { user: "", password: "" },
    validationSchema: Yup.object({
      user: Yup.string().trim().min(3).max(30).required("Requerido"),
      password: Yup.string().trim().min(3).max(30).required("Requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setProcessing(true);
        const { data: dataUser } = await usersLoginPost(values);
        if (!dataUser) return;

        localStorage.setItem(
          process.env.REACT_APP_TOKEN!,
          JSON.stringify(dataUser),
        );
        toaster.success({
          title: "Bienvenido",
          message: dataUser.user.fullName,
          duration: 5000,
          placement: "topRight",
        });
        setModules(dataUser.user.modules);
        setModulesBackup(dataUser.user.modules);
        setClientId(dataUser.user.clientId);
        setClient(dataUser.user.client);
        setProfileId(dataUser.user.profileId);
        setAvatar(dataUser.user.avatar!);
        setFirstName(dataUser.user.firstName);
        setTypeDashboardCode(dataUser.user.typeDashboardCode!);
        setSubscriptionStatus(
          dataUser.user.subscriptionStatusCode ?? null,
          dataUser.user.subscriptionStatus ?? null,
          dataUser.user.daysLeft ?? null,
        );
        setClientDocNumber(dataUser.user.clientDocNumber);
        setApiKeyMagnoSri(dataUser.user.apiKeyMagnoSri ?? null);
        setUserMagnoSri(dataUser.user.userMagnoSri);
        setPasswordMagnoSri(dataUser.user.passwordMagnoSri);
        setRole(dataUser.user.role);
        formik.resetForm();
        setContinuo(true);
      } catch (err: any) {
        const axiosError = err as AxiosError;
        message.error(axiosError.message ?? "Error, comuníquese con Sistemas");
      } finally {
        setProcessing(false);
      }
    },
  });

  if (tokens) return <LoadingFullPage />;

  return (
    <div className="login-root">
      {/* Fondo animado */}
      <AnimatedBackground />

      {/* Tarjeta de login */}
      <Grid
        container
        justifyContent="center"
        alignContent="center"
        className="login-grid-wrapper"
      >
        <Grid item lg={8} md={10} sm={10} xs={11}>
          <div className="login-paper login-paper-split">
            {/* ── Panel izquierdo: presentación del sistema ── */}
            <div className="login-promo">
              <div className="login-promo-inner">
                <img
                  className="login-promo-logo"
                  src={CenefaLogin}
                  width={96}
                  height={96}
                  alt="Stockify"
                  draggable={false}
                />
                <h1 className="login-promo-title">Stockify</h1>
                <p className="login-promo-tagline">
                  Controla tu inventario en tiempo real
                </p>
                <ul className="login-promo-list">
                  <li>Gestión de productos, bodegas y sucursales</li>
                  <li>Movimientos de stock y kardex al instante</li>
                  <li>Reportes y alertas de existencias</li>
                  <li>Facturación electrónica integrada</li>
                </ul>
                <div className="login-promo-accent" />
                <p className="login-promo-foot">CONTROL DE STOCK</p>
              </div>
            </div>

            {/* ── Panel derecho: formulario ── */}
            <div className="login-form-side">
            <form onSubmit={formik.handleSubmit}>
              <Grid
                container
                justifyContent="center"
                alignContent="flex-start"
                className="login-sub-root"
              >
                {/* Logo */}
                <Grid item xs={10} className="login-cenefa">
                  <div className="logo-wrapper">
                    <div className="logo-ring" />
                    <div className="logo-ring logo-ring-2" />
                    <img
                      className="logo-img"
                      src={CenefaLogin}
                      width={140}
                      height={140}
                      alt="Stockify"
                      draggable={false}
                    />
                  </div>
                </Grid>

                {/* Usuario */}
                <Grid item sm={9} xs={11} className="login-texfield">
                  <Input
                    name="user"
                    min={3}
                    max={20}
                    addonBefore={
                      <span className="login-addon-label">Usuario</span>
                    }
                    value={formik.values.user}
                    onChange={formik.handleChange}
                    status={
                      formik.touched.user && formik.errors.user ? "error" : ""
                    }
                  />
                  {formik.touched.user && formik.errors.user && (
                    <p className="login-field-error">{formik.errors.user}</p>
                  )}
                </Grid>

                {/* Contraseña */}
                <Grid item sm={9} xs={11} className="login-texfield">
                  <Input.Password
                    name="password"
                    min={3}
                    max={30}
                    addonBefore={
                      <span className="login-addon-label">Contraseña</span>
                    }
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    status={
                      formik.touched.password && formik.errors.password
                        ? "error"
                        : ""
                    }
                  />
                  {formik.touched.password && formik.errors.password && (
                    <p className="login-field-error">
                      {formik.errors.password}
                    </p>
                  )}
                </Grid>

                {/* Botón */}
                <Grid item sm={9} xs={11} className="login-button">
                  <Button
                    className="login-btn"
                    htmlType="submit"
                    loading={isProcessing}
                    disabled={isProcessing}
                  >
                    Entrar
                  </Button>
                </Grid>

                {/* Acento inferior */}
                <Grid item xs={12}>
                  <div className="login-accent-bar" />
                  <p className="login-footer-text">CONTROL DE STOCK</p>
                </Grid>
              </Grid>
            </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};
